/**
 * Generic application-level cache backed by in-memory Map + IndexedDB.
 *
 * - In-memory Map acts as a fast L1 cache (cleared on page refresh).
 * - IndexedDB acts as an L2 store with a configurable TTL so cached data
 *   survives the micro-navigations that React Router does without requiring a
 *   full network round-trip.
 *
 * Usage:
 *   import { AppCache } from '../shared';
 *   await AppCache.set('my-key', data);
 *   const data = await AppCache.get('my-key');
 */

const DB_NAME = 'mediaops-app-cache-db';
const STORE_NAME = 'app-cache-store';
const DB_VERSION = 1;

// ── IndexedDB helpers ─────────────────────────────────────────────────────────

function openDb() {
  return new Promise((resolve, reject) => {
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key) {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const r = store.get(key);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
  } catch {
    return null;
  }
}

async function idbSet(key, value) {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const r = store.put(value, key);
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  } catch {
    return;
  }
}

async function idbDelete(key) {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const r = store.delete(key);
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  } catch {
    return;
  }
}

// ── In-memory L1 cache ────────────────────────────────────────────────────────

const memory = new Map();

// ── Public API ────────────────────────────────────────────────────────────────

export const AppCache = {
  /**
   * Retrieve data by key.
   * Returns null when the entry is absent or has expired.
   * @param {string} key
   * @returns {Promise<any|null>}
   */
  async get(key) {
    const now = Date.now();

    // L1 – memory
    const mem = memory.get(key);
    if (mem) {
      if (mem.expires > now) return mem.data;
      memory.delete(key);
    }

    // L2 – IndexedDB
    const entry = await idbGet(key).catch(() => null);
    if (!entry) return null;
    if (entry.expires && entry.expires > now) {
      memory.set(key, { data: entry.data, expires: entry.expires });
      return entry.data;
    }

    // Entry is stale – clean up
    await idbDelete(key).catch(() => {});
    return null;
  },

  /**
   * Store data under key with a time-to-live.
   * Default TTL is 1 hour.
   * @param {string} key
   * @param {any} data
   * @param {number} [ttlMs]
   * @returns {Promise<void>}
   */
  async set(key, data, ttlMs = 60 * 60 * 1000) {
    const expires = Date.now() + ttlMs;
    memory.set(key, { data, expires });
    await idbSet(key, { data, expires }).catch(() => {});
  },

  /**
   * Remove a specific entry.
   * @param {string} key
   * @returns {Promise<void>}
   */
  async delete(key) {
    memory.delete(key);
    await idbDelete(key).catch(() => {});
  },

  /**
   * Clear only the in-memory L1 cache.
   * Useful when you want subsequent reads to re-validate from IndexedDB.
   */
  clearMemory() {
    memory.clear();
  },
};
